import React, { PropsWithChildren, useMemo } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { Icon, useTheme } from '@ui-kitten/components';

import themeStyle from '../Theme/styles';

import Button from './Button';
import ResponsiveText from './ResponsiveText';

type IconProps = {
  disabled?: boolean;
  name?: string;
  svg?: React.FC<SvgProps>; // result of 'require()'
  color?: string;
  size?: number;
  style?: StyleProp<ViewStyle>;
  onPress?: () => void;
};
type ListItemProps = {
  accessoryLeft?: React.ReactNode;
  accessoryRight?: React.ReactNode;
  contentContainerStyle?: StyleProp<ViewStyle>;
  flex?: boolean;
  isLeftIconEva?: boolean;
  isRightIconEva?: boolean;
  leftIcon?: IconProps;
  rightIcon?: IconProps;
  spacing?: number;
  subTitle?: string | number | React.ReactNode;
  title: string | React.ReactNode;
  onPress?: (data?: unknown) => void;
  [key: string]: any;
};

const Container = ({
  children,
  hitSlop,
  onPress,
  ...rest
}: PropsWithChildren<{
  hitSlop?: number;
  onPress: () => void;
  [key: string]: any;
}>) => {
  return typeof onPress === 'function' ? (
    <Button {...rest} hitSlop={hitSlop} onPress={onPress}>
      {children}
    </Button>
  ) : (
    <View {...rest}>{children}</View>
  );
};

const ListItem = ({
  accessoryLeft,
  accessoryRight,
  contentContainerStyle,
  flex = true,
  isLeftIconEva = true,
  isRightIconEva = true,
  leftIcon,
  rightIcon,
  spacing,
  subTitle,
  title,
  onPress,
  ...rest
}: ListItemProps) => {
  const theme = useTheme();
  const LeftSvg = leftIcon?.svg;
  const leftIconSize = leftIcon?.size || 24;
  const rightIconSize = rightIcon?.size || 24;
  const leftIconStyle = useMemo(
    () => ({ width: leftIconSize, height: leftIconSize }),
    [leftIconSize],
  );
  const rightIconStyle = useMemo(
    () => ({ width: rightIconSize, height: rightIconSize }),
    [rightIconSize],
  );
  return (
    <Container {...rest} onPress={onPress} plain>
      <View
        style={[
          flex && themeStyle.flex1,
          themeStyle.flexDirectionRowCenter,
          StyleSheet.flatten(contentContainerStyle),
        ]}
      >
        {accessoryLeft}
        {(Boolean(leftIcon?.name) || Boolean(LeftSvg)) && (
          <Container
            disabled={leftIcon?.disabled}
            hitSlop={10}
            style={[
              spacing
                ? { marginRight: spacing }
                : themeStyle.spacingRightMedium,
              !isLeftIconEva && leftIconStyle, // if not eva, this to match the eva size icon
              leftIcon?.style,
            ]}
            onPress={leftIcon?.onPress}
            plain
          >
            {Boolean(LeftSvg) && (
              <View style={themeStyle.flexDirectionRowCenterCenter}>
                <LeftSvg
                  fill={leftIcon.color || theme['text-basic-color']}
                  width={leftIconSize}
                  height={leftIconSize}
                />
              </View>
            )}
            {Boolean(leftIcon?.name) && (
              <Icon
                fill={leftIcon.color || theme['text-basic-color']}
                name={leftIcon.name}
                pack={isLeftIconEva ? 'eva' : 'fa'}
                style={leftIconStyle}
              />
            )}
          </Container>
        )}
        <View style={flex && themeStyle.flex1}>
          {typeof title === 'string' ? (
            <ResponsiveText>{title}</ResponsiveText>
          ) : (
            title
          )}
          {Boolean(subTitle) &&
            (typeof subTitle === 'string' ? (
              <ResponsiveText category="c1" numberOfLines={1}>
                {subTitle}
              </ResponsiveText>
            ) : (
              subTitle
            ))}
        </View>
        {Boolean(rightIcon?.name) && (
          <Container
            disabled={rightIcon?.disabled}
            hitSlop={10}
            style={[
              spacing ? { marginLeft: spacing } : themeStyle.spacingLeftMedium,
              !isRightIconEva && rightIconStyle,
              rightIcon?.style,
            ]}
            onPress={rightIcon?.onPress}
            plain
          >
            <Icon
              fill={rightIcon.color || theme['text-basic-color']}
              name={rightIcon.name}
              pack={isRightIconEva ? 'eva' : 'fa'}
              style={rightIconStyle}
            />
          </Container>
        )}
        {accessoryRight}
      </View>
    </Container>
  );
};

export default ListItem;
